import {Component, effect, ElementRef, inject} from '@angular/core';
import {Snack, SnackbarService, SNACK_DURATION_MS} from 'src/app/services/ui/snackbar.service';
import {RightSliderService} from 'src/app/services/ui/right-slider.service';
import {CountdownCloseButtonComponent} from '../../design-system/countdown-close-button/countdown-close-button.component';

/**
 * The app's single message host, mounted once in the root component. A popover, so in the browser's top layer like the
 * CDK dialogs: shown again for each new message, it paints above a dialog opened since.
 */
@Component({
	selector: 'app-snackbar',
	imports: [CountdownCloseButtonComponent],
	templateUrl: './snackbar.component.html',
	styleUrl: './snackbar.component.scss',
	host: {popover: 'manual', '(window:resize)': 'place()'},
})
export class SnackbarComponent {
	protected readonly snackbar = inject(SnackbarService);
	protected readonly duration = SNACK_DURATION_MS;
	private readonly rightSlider = inject(RightSliderService);
	private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

	constructor() {
		// A new message, or a drawer that opens or closes under one, centers it again.
		effect(() => {
			this.snackbar.snacks();
			this.rightSlider.open();
			this.place();
		});
		let raisedFor: number | undefined;
		effect(() => {
			const newest = this.snackbar.snacks().at(-1);
			if (newest && newest.id !== raisedFor) {
				raisedFor = newest.id;
				// Top layer entries paint in the order they were shown.
				if (this.host.matches(':popover-open')) {
					this.host.hidePopover();
				}
				this.host.showPopover();
			}
		});
	}

	/**
	 * Read as a message goes. The repeat that replaced it plays its entrance in its place, so it goes at once: no second
	 * card sliding out next to the new one.
	 */
	protected leaveAnimation(gone: Snack): string {
		return this.snackbar.snacks().some(snack => snack.message === gone.message && snack.type === gone.type) ? '' : 'snack-leave';
	}

	/**
	 * Centers the messages where they come from, as each page and drawer did with its own host: on the drawer when one
	 * is open, else on the page's content, right of the menu; on the screen when there is neither.
	 */
	protected place(): void {
		const anchor = document.querySelector<HTMLElement>(this.rightSlider.open() ? '.right-drawer' : '.main-container');
		// Layout offsets, not the bounding box: a drawer still sliding in is translated
		const parentLeft = anchor?.offsetParent?.getBoundingClientRect().left ?? 0;
		this.host.style.left = anchor ? `${parentLeft + anchor.offsetLeft + anchor.offsetWidth / 2}px` : '';
	}
}
